import type { DocKind } from '../core/documents';
import { decodeText, NotTextError, type DecodedText } from './codec';

/**
 * The file capability boundary.
 *
 * Everything the editor needs from "the disk" goes through `FileAdapter`. The
 * browser implementation below uses the File System Access API where it exists
 * and falls back to a file input plus downloads elsewhere. A desktop shell
 * (e.g. Tauri) provides another implementation of the same interface, with
 * `FileRef` being a path instead of a handle.
 */

/** Opaque, platform-specific pointer to a writable file. Must be structured-cloneable. */
export type FileRef = unknown;

export interface OpenedFile {
  name: string;
  decoded: DecodedText;
  lastModified: number;
  /** Present only when the platform can later write back to this exact file. */
  ref?: FileRef;
}

export interface OpenResult {
  files: OpenedFile[];
  /** Names of files that could not be opened as text. */
  rejected: string[];
}

export type SaveAsResult =
  | { method: 'file'; name: string; ref: FileRef; lastModified: number }
  | { method: 'download'; name: string };

export class FileConflictError extends Error {
  constructor() {
    super('The file changed on disk.');
  }
}

export class FilePermissionError extends Error {
  constructor() {
    super('Permission to write the file was not granted.');
  }
}

export interface FileAdapter {
  /** True when saving can write back to the original file instead of downloading a copy. */
  readonly writesInPlace: boolean;
  pickFiles(): Promise<OpenResult>;
  filesFromDrop(data: DataTransfer): Promise<OpenResult>;
  /**
   * Write to a linked file. Throws `FileConflictError` when the file changed
   * since `expectedModified` (unless `force`), `FilePermissionError` when access is refused.
   */
  write(ref: FileRef, data: Uint8Array<ArrayBuffer>, expectedModified?: number, force?: boolean): Promise<{ lastModified: number }>;
  saveAs(suggestedName: string, kind: DocKind, data: Uint8Array<ArrayBuffer>): Promise<SaveAsResult | null>;
  sameFile(a: FileRef, b: FileRef): Promise<boolean>;
}

const MAX_BYTES = 25 * 1024 * 1024;
const ACCEPT = '.txt,.md,.markdown,.mdown,.mkd,.text,text/plain,text/markdown';

// --- File System Access API surface (not yet in TypeScript's DOM lib) ---

interface PickerType {
  description: string;
  accept: Record<string, string[]>;
}
interface FsWindow {
  showOpenFilePicker?: (options: { multiple?: boolean; types?: PickerType[]; excludeAcceptAllOption?: boolean }) => Promise<FileSystemFileHandle[]>;
  showSaveFilePicker?: (options: { suggestedName?: string; types?: PickerType[] }) => Promise<FileSystemFileHandle>;
}
interface PermissionedHandle extends FileSystemFileHandle {
  queryPermission?: (d: { mode: 'readwrite' }) => Promise<PermissionState>;
  requestPermission?: (d: { mode: 'readwrite' }) => Promise<PermissionState>;
  createWritable(): Promise<FileSystemWritableFileStream>;
}
interface HandleItem extends DataTransferItem {
  getAsFileSystemHandle?: () => Promise<FileSystemHandle | null>;
}

const TEXT_TYPES: PickerType[] = [
  { description: 'Text and Markdown', accept: { 'text/plain': ['.txt', '.text'], 'text/markdown': ['.md', '.markdown', '.mdown', '.mkd'] } },
];

function typesFor(kind: DocKind): PickerType[] {
  return kind === 'md'
    ? [{ description: 'Markdown', accept: { 'text/markdown': ['.md'] } }]
    : [{ description: 'Plain text', accept: { 'text/plain': ['.txt'] } }];
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

async function readFile(file: File, ref?: FileRef): Promise<OpenedFile> {
  if (file.size > MAX_BYTES) throw new NotTextError();
  const decoded = decodeText(new Uint8Array(await file.arrayBuffer()));
  return { name: file.name, decoded, lastModified: file.lastModified, ref };
}

async function collect(sources: Array<() => Promise<OpenedFile>>, names: string[]): Promise<OpenResult> {
  const result: OpenResult = { files: [], rejected: [] };
  const settled = await Promise.allSettled(sources.map((s) => s()));
  settled.forEach((s, i) => {
    if (s.status === 'fulfilled') result.files.push(s.value);
    else result.rejected.push(names[i]);
  });
  return result;
}

function pickWithInput(): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = ACCEPT;
    input.style.display = 'none';
    const finish = (files: File[]) => {
      input.remove();
      resolve(files);
    };
    input.addEventListener('change', () => finish(Array.from(input.files ?? [])), { once: true });
    input.addEventListener('cancel', () => finish([]), { once: true });
    document.body.append(input);
    input.click();
  });
}

export function downloadText(name: string, kind: DocKind, data: Uint8Array<ArrayBuffer>): void {
  const type = kind === 'md' ? 'text/markdown;charset=utf-8' : 'text/plain;charset=utf-8';
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function createBrowserFileAdapter(win: Window & FsWindow = window): FileAdapter {
  const fsAccess = typeof win.showOpenFilePicker === 'function' && typeof win.showSaveFilePicker === 'function';

  return {
    writesInPlace: fsAccess,

    async pickFiles() {
      if (fsAccess) {
        let handles: FileSystemFileHandle[];
        try {
          handles = await win.showOpenFilePicker!({ multiple: true, types: TEXT_TYPES });
        } catch (error) {
          if (isAbort(error)) return { files: [], rejected: [] };
          throw error;
        }
        return collect(
          handles.map((h) => async () => readFile(await h.getFile(), h)),
          handles.map((h) => h.name),
        );
      }
      const files = await pickWithInput();
      return collect(files.map((f) => () => readFile(f)), files.map((f) => f.name));
    },

    async filesFromDrop(data) {
      const items = Array.from(data.items).filter((i) => i.kind === 'file') as HandleItem[];
      // Handles must be requested synchronously within the drop event, before any await.
      const pending = items.map((item) => ({
        handle: fsAccess && item.getAsFileSystemHandle ? item.getAsFileSystemHandle() : null,
        file: item.getAsFile(),
      }));
      return collect(
        pending.map(({ handle, file }) => async () => {
          const h = handle ? await handle.catch(() => null) : null;
          if (h && h.kind === 'file') return readFile(await (h as FileSystemFileHandle).getFile(), h);
          if (!file) throw new NotTextError();
          return readFile(file);
        }),
        pending.map(({ file }) => file?.name ?? 'File'),
      );
    },

    async write(ref, data, expectedModified, force = false) {
      const handle = ref as PermissionedHandle;
      const query = await handle.queryPermission?.({ mode: 'readwrite' });
      if (query && query !== 'granted') {
        const granted = await handle.requestPermission?.({ mode: 'readwrite' }).catch(() => 'denied' as const);
        if (granted !== 'granted') throw new FilePermissionError();
      }
      if (!force && expectedModified !== undefined) {
        const current = await handle.getFile();
        if (current.lastModified !== expectedModified) throw new FileConflictError();
      }
      const writable = await handle.createWritable();
      await writable.write(data);
      await writable.close();
      return { lastModified: (await handle.getFile()).lastModified };
    },

    async saveAs(suggestedName, kind, data) {
      if (!fsAccess) {
        downloadText(suggestedName, kind, data);
        return { method: 'download', name: suggestedName };
      }
      let handle: FileSystemFileHandle;
      try {
        handle = await win.showSaveFilePicker!({ suggestedName, types: typesFor(kind) });
      } catch (error) {
        if (isAbort(error)) return null;
        throw error;
      }
      const writable = await (handle as PermissionedHandle).createWritable();
      await writable.write(data);
      await writable.close();
      return { method: 'file', name: handle.name, ref: handle, lastModified: (await handle.getFile()).lastModified };
    },

    async sameFile(a, b) {
      const ha = a as FileSystemFileHandle | undefined;
      const hb = b as FileSystemFileHandle | undefined;
      if (!ha || !hb || typeof ha.isSameEntry !== 'function') return false;
      return ha.isSameEntry(hb).catch(() => false);
    },
  };
}

/**
 * What a drag carries, judged while it is still in the air (browsers reveal
 * only the MIME types then, not the names). `files` when at least one item
 * may be text, `reject` when every item is plainly something else (an image,
 * a PDF), `none` when it is not a file drag at all (text, a tab).
 */
export function dragVerdict(data: Pick<DataTransfer, 'types' | 'items'> | null): 'files' | 'reject' | 'none' {
  if (!data || !Array.from(data.types).includes('Files')) return 'none';
  const items = Array.from(data.items ?? []).filter((i) => i.kind === 'file');
  if (!items.length) return 'files';
  const textLike = (type: string) => type === '' || type.startsWith('text/') || /markdown|json|xml|yaml|x-sh/.test(type);
  return items.some((i) => textLike(i.type)) ? 'files' : 'reject';
}
